import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * /admin/finanzas/conciliar
 *
 * Cola de aprobación: el sistema sugiere matches entre movimientos bancarios
 * sin conciliar y asientos contables candidatos. Roberto aprueba uno por uno
 * o en bulk los 100%. NUNCA se concilia automático sin aprobación humana.
 */
export default function ConciliarBanco() {
  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [sugerencias, setSugerencias] = useState([])
  const [sinSugerencia, setSinSugerencia] = useState([])
  const [loading, setLoading] = useState(true)
  const [procesando, setProcesando] = useState(false)
  const [seleccionados, setSeleccionados] = useState(() => new Set())

  useEffect(() => { cargar() }, [filtroMes])

  async function cargar() {
    setLoading(true)
    setSeleccionados(new Set())
    // Obtener todas las sugerencias
    const { data: sugs, error: e1 } = await supabase.rpc('sugerir_matches_bancarios', { p_mes: filtroMes })
    if (e1) { console.error(e1); setSugerencias([]) }
    else {
      // Agrupar por movimiento_id, quedarse con la mejor sugerencia
      const porMov = new Map()
      for (const s of (sugs || [])) {
        const ex = porMov.get(s.movimiento_id)
        if (!ex || s.score > ex.score) porMov.set(s.movimiento_id, s)
      }
      setSugerencias(Array.from(porMov.values()).sort((a, b) => b.score - a.score))
    }
    // Movimientos sin ninguna sugerencia
    const desde = `${filtroMes}-01`
    // Bug fix: junio/abr/sep/nov no tienen 31. Usamos primer día del mes siguiente con .lt()
    const [_y, _m] = filtroMes.split('-').map(Number)
    const hasta = new Date(_y, _m, 1).toISOString().slice(0, 10)
    const { data: movs } = await supabase
      .from('movimientos_bancarios').select('*')
      .eq('conciliado', false)
      .gte('fecha', desde).lt('fecha', hasta)
      .not('categoria_sugerida', 'in', '(saldo_apertura,retencion_iva)')
    const idsConSug = new Set((sugs || []).map(s => s.movimiento_id))
    setSinSugerencia((movs || []).filter(m => !idsConSug.has(m.id)))
    setLoading(false)
  }

  const verdes = useMemo(() => sugerencias.filter(s => s.score >= 100), [sugerencias])
  const amarillos = useMemo(() => sugerencias.filter(s => s.score >= 70 && s.score < 100), [sugerencias])
  const naranjas = useMemo(() => sugerencias.filter(s => s.score < 70), [sugerencias])

  async function aprobar(sug) {
    if (procesando) return
    setProcesando(true)
    try {
      const { error } = await supabase.rpc('conciliar_movimiento_bancario', {
        p_movimiento_id: sug.movimiento_id,
        p_asiento_relacionado_id: sug.asiento_id,
      })
      if (error) throw error
      cargar()
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setProcesando(false)
    }
  }

  async function aprobarSeleccionados() {
    if (seleccionados.size === 0) return
    if (!confirm(`¿Aprobar y conciliar ${seleccionados.size} movimientos?`)) return
    setProcesando(true)
    try {
      let ok = 0, fail = 0
      for (const movId of seleccionados) {
        const sug = sugerencias.find(s => s.movimiento_id === movId)
        if (!sug) continue
        try {
          const { error } = await supabase.rpc('conciliar_movimiento_bancario', {
            p_movimiento_id: sug.movimiento_id,
            p_asiento_relacionado_id: sug.asiento_id,
          })
          if (error) fail++
          else ok++
        } catch { fail++ }
      }
      alert(`Conciliados: ${ok}\nFallidos: ${fail}`)
      cargar()
    } finally {
      setProcesando(false)
    }
  }

  function toggleSeleccion(movId) {
    setSeleccionados(prev => {
      const next = new Set(prev)
      if (next.has(movId)) next.delete(movId)
      else next.add(movId)
      return next
    })
  }

  function seleccionarTodosVerdes() {
    setSeleccionados(new Set(verdes.map(v => v.movimiento_id)))
  }

  async function conciliarSinSugerencia(mov) {
    const codigo = prompt(
      `Movimiento sin sugerencia: ${mov.concepto}\n` +
      `${mov.credito > 0 ? 'Entrada' : 'Salida'} $${fmt(mov.credito || mov.debito)} fecha ${mov.fecha}\n\n` +
      `Indicá la cuenta contable contrapeso (ej: 5.3.02 para gasto administrativo):`
    )
    if (!codigo) return
    setProcesando(true)
    try {
      const { error } = await supabase.rpc('conciliar_movimiento_bancario', {
        p_movimiento_id: mov.id,
        p_cuenta_contrapeso: codigo,
      })
      if (error) throw error
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setProcesando(false) }
  }

  return (
    <AdminLayout>
      <div className="cnc-wrap">
        <div className="cnc-headline">
          <div>
            <h1>Conciliar banco ↔ asientos</h1>
            <p>El sistema sugiere matches. Vos aprobás. Cero acceso al banco — sólo lo que ya está en DB.</p>
          </div>
          <div className="cnc-toolbar">
            <label className="cnc-fil">
              <span>Mes</span>
              <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
            </label>
            <Link to="/admin/finanzas/conciliacion" className="cnc-btn-link">← Volver a importación</Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="cnc-kpis">
          <KpiCard label="Listos (100%)"   value={verdes.length}    color="#16a34a" sub="Aprobá todos en bulk" />
          <KpiCard label="Revisar (70-99%)" value={amarillos.length} color="#d97706" sub="Confirmá uno por uno" />
          <KpiCard label="Inciertos (<70%)" value={naranjas.length}  color="#dc2626" sub="Verificá el match" />
          <KpiCard label="Sin sugerencia"   value={sinSugerencia.length} color="#475569" sub="Concilia manual" />
        </div>

        {loading ? <div className="cnc-loading">Calculando matches…</div> : (
          <>
            {/* Bulk action */}
            {verdes.length > 0 && (
              <div className="cnc-bulk">
                <span>📋 <b>{verdes.length} movimientos con match 100%</b> listos para aprobar en bulk</span>
                <button className="cnc-btn-ghost" onClick={seleccionarTodosVerdes}>Seleccionar todos</button>
                <button
                  className="cnc-btn-primary"
                  disabled={seleccionados.size === 0 || procesando}
                  onClick={aprobarSeleccionados}
                >
                  {procesando ? 'Procesando…' : `✓ Aprobar ${seleccionados.size} seleccionados`}
                </button>
              </div>
            )}

            {/* Tabla unificada */}
            <Seccion titulo="✓ Listos para conciliar (100%)" color="#16a34a" items={verdes}
                     onAprobar={aprobar} onToggle={toggleSeleccion} seleccionados={seleccionados} mostrarCheckbox procesando={procesando} />

            <Seccion titulo="🟡 Para revisar (70-99%)" color="#d97706" items={amarillos}
                     onAprobar={aprobar} onToggle={toggleSeleccion} seleccionados={seleccionados} procesando={procesando} />

            <Seccion titulo="⚠ Inciertos (<70%)" color="#dc2626" items={naranjas}
                     onAprobar={aprobar} onToggle={toggleSeleccion} seleccionados={seleccionados} procesando={procesando} />

            {/* Movimientos sin sugerencia */}
            {sinSugerencia.length > 0 && (
              <>
                <h2 className="cnc-h2" style={{ color: '#475569' }}>⚪ Sin sugerencia ({sinSugerencia.length})</h2>
                <p className="cnc-h2-sub">No encontré asiento contable candidato. Conciliá manual indicando la cuenta contrapeso.</p>
                <div className="cnc-tabla-wrap">
                  <table className="cnc-tabla">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Concepto</th>
                        <th>Categoría</th>
                        <th style={{ textAlign: 'right' }}>Débito</th>
                        <th style={{ textAlign: 'right' }}>Crédito</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sinSugerencia.map(m => (
                        <tr key={m.id}>
                          <td>{m.fecha}</td>
                          <td>{m.concepto}</td>
                          <td style={{ fontSize: 11, color: '#64748b' }}>{m.categoria_sugerida}</td>
                          <td style={{ textAlign: 'right', color: '#dc2626' }}>{m.debito > 0 ? '$' + fmt(m.debito) : '—'}</td>
                          <td style={{ textAlign: 'right', color: '#16a34a' }}>{m.credito > 0 ? '$' + fmt(m.credito) : '—'}</td>
                          <td><button className="cnc-btn-manual" onClick={() => conciliarSinSugerencia(m)} disabled={procesando}>Conciliar manual</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {verdes.length === 0 && amarillos.length === 0 && naranjas.length === 0 && sinSugerencia.length === 0 && (
              <div className="cnc-empty">
                <strong>🎉 Sin pendientes</strong>
                <p>Todos los movimientos de {filtroMes} están conciliados, o todavía no importaste el extracto del mes.</p>
                <Link to="/admin/finanzas/conciliacion" className="cnc-btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: 12 }}>Ir a importar extracto</Link>
              </div>
            )}
          </>
        )}
      </div>
      <style>{CSS}</style>
    </AdminLayout>
  )
}

function Seccion({ titulo, color, items, onAprobar, onToggle, seleccionados, mostrarCheckbox, procesando }) {
  if (items.length === 0) return null
  return (
    <>
      <h2 className="cnc-h2" style={{ color }}>{titulo} ({items.length})</h2>
      <div className="cnc-tabla-wrap">
        <table className="cnc-tabla">
          <thead>
            <tr>
              {mostrarCheckbox && <th style={{ width: 30 }}></th>}
              <th>Movimiento bancario</th>
              <th>↔</th>
              <th>Asiento contable sugerido</th>
              <th style={{ textAlign: 'right' }}>Monto</th>
              <th style={{ textAlign: 'center' }}>Score</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={`${s.movimiento_id}-${s.asiento_id}`}>
                {mostrarCheckbox && (
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={seleccionados.has(s.movimiento_id)} onChange={() => onToggle(s.movimiento_id)} />
                  </td>
                )}
                <td>
                  <div className="cnc-mov-fecha">{s.movimiento_fecha}</div>
                  <div className="cnc-mov-concepto">{s.movimiento_concepto}</div>
                  <div className="cnc-mov-monto">
                    {s.movimiento_credito > 0 ? '+ $' + fmt(s.movimiento_credito) : '− $' + fmt(s.movimiento_debito)}
                  </div>
                </td>
                <td style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 18 }}>↔</td>
                <td>
                  <div className="cnc-asi-num">#{s.asiento_numero} · {s.asiento_fecha}</div>
                  <div className="cnc-asi-concepto">{s.asiento_concepto}</div>
                  <div className="cnc-asi-cuenta"><code>{s.asiento_cuenta_codigo}</code> {s.asiento_cuenta_nombre}</div>
                </td>
                <td style={{ textAlign: 'right' }}>${fmt(s.asiento_monto)}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className="cnc-score" style={{ background: color + '20', color }}>
                    {s.score}
                  </span>
                  <div className="cnc-motivo">{s.motivo}</div>
                </td>
                <td>
                  <button className="cnc-btn-approve" onClick={() => onAprobar(s)} disabled={procesando}>✓ Aprobar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="cnc-kpi">
      <div className="cnc-kpi-l">{label}</div>
      <div className="cnc-kpi-v" style={{ color }}>{value}</div>
      {sub && <div className="cnc-kpi-s">{sub}</div>}
    </div>
  )
}

const CSS = `
.cnc-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.cnc-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 18px }
.cnc-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.cnc-headline p { font-size: 13px; color: #64748b; margin: 0 }
.cnc-toolbar { display: flex; gap: 12px; align-items: flex-end }
.cnc-fil { display: flex; flex-direction: column; gap: 3px }
.cnc-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.cnc-fil input { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit }
.cnc-btn-link { padding: 8px 12px; color: #475569; text-decoration: none; font-size: 13px; font-weight: 700; border: 1px solid #cbd5e1; border-radius: 7px }

.cnc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 18px }
.cnc-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.cnc-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.cnc-kpi-v { font-size: 28px; font-weight: 900 }
.cnc-kpi-s { font-size: 11px; color: #64748b }

.cnc-bulk { display: flex; gap: 10px; align-items: center; padding: 12px 16px; background: #ecfdf5; border: 1px solid #6ee7b7; border-radius: 10px; margin-bottom: 14px; font-size: 13px; color: #065f46; flex-wrap: wrap }
.cnc-bulk b { color: #064e3b }

.cnc-btn-primary { background: #16a34a; color: #fff; border: 0; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.cnc-btn-primary:hover:not(:disabled) { background: #15803d }
.cnc-btn-primary:disabled { opacity: .6; cursor: not-allowed }
.cnc-btn-ghost { background: transparent; border: 1px solid #6ee7b7; color: #065f46; padding: 7px 12px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit }
.cnc-btn-approve { background: #16a34a; color: #fff; border: 0; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 800; cursor: pointer; font-family: inherit }
.cnc-btn-approve:hover:not(:disabled) { background: #15803d }
.cnc-btn-approve:disabled { opacity: .6; cursor: not-allowed }
.cnc-btn-manual { background: #1A3461; color: #fff; border: 0; padding: 5px 11px; border-radius: 6px; font-size: 11px; font-weight: 800; cursor: pointer; font-family: inherit }

.cnc-h2 { font-size: 16px; font-weight: 900; margin: 24px 0 4px }
.cnc-h2-sub { font-size: 12.5px; color: #64748b; margin: 0 0 12px }

.cnc-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto; margin-bottom: 18px }
.cnc-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px; min-width: 1100px }
.cnc-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.cnc-tabla td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461; vertical-align: top }

.cnc-mov-fecha, .cnc-asi-num { font-size: 11px; color: #64748b; font-weight: 700 }
.cnc-mov-concepto, .cnc-asi-concepto { font-size: 13px; font-weight: 700; color: #1A3461; margin-top: 2px }
.cnc-mov-monto { font-size: 13px; font-weight: 800; margin-top: 4px; color: #1A3461 }
.cnc-asi-cuenta { font-size: 11px; color: #64748b; margin-top: 4px }
.cnc-asi-cuenta code { background: #e0e7ff; color: #3730a3; padding: 1px 5px; border-radius: 3px; font-size: 10.5px }

.cnc-score { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 14px; font-weight: 900 }
.cnc-motivo { font-size: 10.5px; color: #94a3b8; margin-top: 4px; max-width: 200px }

.cnc-loading, .cnc-empty { padding: 50px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.cnc-empty strong { display: block; color: #1A3461; font-size: 16px; margin-bottom: 6px }
.cnc-empty p { font-size: 13px; color: #64748b }
`
